import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFlatFileUploadLink } from '../hooks/useSource';
import { sourceService } from '../services/source.service';
import { S3Uploader, type UploadedFile } from '../components/s3uploader/S3Uploader';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle2, AlertTriangle, Upload } from 'lucide-react';
import { usePageBranding } from '../hooks/usePageBranding';
import pliableLogo from '../assets/images/pliable/logo_with_text_black.png';

const DEFAULT_TITLE = 'Pliable';

type PageState = 'idle' | 'processing' | 'complete' | 'error';

export default function FlatFileUploadPage() {
  const { code } = useParams<{ code: string }>();
  const { data: linkDetails, isLoading, error } = useFlatFileUploadLink(code);
  const [pageState, setPageState] = useState<PageState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [descriptionSent, setDescriptionSent] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentFileIdRef = useRef<string | null>(null);

  const logoPath = linkDetails?.branding?.logo_path || pliableLogo;
  const brandName = linkDetails?.branding?.page_title || DEFAULT_TITLE;
  const askForDescription = linkDetails?.ask_for_description ?? true;

  usePageBranding(linkDetails?.branding);

  useEffect(() => {
    return () => {
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, []);

  const pollJobStatus = useCallback(async (jobId: string) => {
    if (!code) return;
    try {
      const result = await sourceService.getFlatFileUploadJobStatus(code, jobId);
      if (result.job_status === 'finished') {
        setPageState('complete');
      } else if (result.job_status === 'failed') {
        setPageState('error');
        setErrorMessage(result.error_message || 'Processing failed');
      } else {
        pollingRef.current = setTimeout(() => pollJobStatus(jobId), 2000);
      }
    } catch {
      setPageState('error');
      setErrorMessage('Failed to check processing status');
    }
  }, [code]);

  const handleUploadComplete = useCallback(async (file: UploadedFile) => {
    if (!code) return;
    currentFileIdRef.current = file.id;
    setPageState('processing');
    try {
      const { job_id } = await sourceService.processFlatFileUpload(code, file.id);
      pollJobStatus(job_id);
    } catch {
      setPageState('error');
      setErrorMessage('Failed to start file processing');
    }
  }, [code, pollJobStatus]);

  const handleSendDescription = useCallback(async () => {
    if (!code || !currentFileIdRef.current || !description.trim()) return;
    try {
      await sourceService.describeFlatFileUpload(code, currentFileIdRef.current, description.trim());
      setDescriptionSent(true);
    } catch {
      // non-critical, don't block the flow
    }
  }, [code, description]);

  const handleUploadAnother = useCallback(() => {
    setPageState('idle');
    setErrorMessage(null);
    setDescription('');
    setDescriptionSent(false);
    currentFileIdRef.current = null;
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !linkDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
            <h2 className="text-lg font-semibold">Link Unavailable</h2>
            <p className="text-sm text-muted-foreground">
              This link may have expired or is invalid.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center space-y-3">
          <img src={logoPath} alt={brandName} className="h-8 mx-auto" />
          <CardTitle className="text-xl">
            {linkDetails.title || `Upload to ${linkDetails.source_name}`}
          </CardTitle>
          {linkDetails.description && (
            <p className="text-sm text-muted-foreground">{linkDetails.description}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {pageState === 'idle' && (
            <>
              <S3Uploader
                onUploadComplete={handleUploadComplete}
                initUploadEndpoint={`/sources/flat-file-upload-link/${code}/init-upload`}
                label="Drag and drop a CSV, Excel, or Parquet file here, or click to browse"
              />
              <p className="text-xs text-muted-foreground text-center">
                Supported formats: CSV, Excel (XLSX), Parquet. Max file size: 250MB.
              </p>
            </>
          )}

          {pageState === 'processing' && (
            <div className="text-center space-y-4 py-6">
              <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
              <p className="text-sm text-muted-foreground">Processing your file...</p>
              <p className="text-xs text-muted-foreground">This may take a minute depending on file size.</p>
              {askForDescription && !descriptionSent && (
                <div className="text-left space-y-2 pt-2">
                  <label className="text-sm font-medium">What&apos;s in this file? <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Textarea
                    placeholder="e.g. Monthly sales data from our POS system, January 2026"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendDescription}
                    disabled={!description.trim()}
                  >
                    Save Description
                  </Button>
                </div>
              )}
              {askForDescription && descriptionSent && (
                <p className="text-sm text-green-600">Description saved!</p>
              )}
            </div>
          )}

          {pageState === 'complete' && (
            <div className="text-center space-y-4 py-6">
              <CheckCircle2 className="h-10 w-10 text-green-500 mx-auto" />
              <h3 className="text-lg font-semibold">Upload Complete</h3>
              <p className="text-sm text-muted-foreground">
                Your file has been uploaded and processed successfully. The requesting team has been notified.
              </p>
              {askForDescription && !descriptionSent && (
                <div className="text-left space-y-2 pt-2">
                  <label className="text-sm font-medium">What&apos;s in this file? <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Textarea
                    placeholder="e.g. Monthly sales data from our POS system, January 2026"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSendDescription}
                    disabled={!description.trim()}
                  >
                    Save Description
                  </Button>
                </div>
              )}
              {askForDescription && descriptionSent && (
                <p className="text-sm text-green-600">Description saved!</p>
              )}
              {linkDetails.allow_multiple_uploads && (
                <Button variant="outline" onClick={handleUploadAnother}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Another File
                </Button>
              )}
            </div>
          )}

          {pageState === 'error' && (
            <div className="text-center space-y-4 py-6">
              <AlertTriangle className="h-10 w-10 text-destructive mx-auto" />
              <h3 className="text-lg font-semibold">Processing Error</h3>
              <p className="text-sm text-destructive">{errorMessage}</p>
              <Button variant="outline" onClick={handleUploadAnother}>
                Try Again
              </Button>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground pt-2">
            Powered by {brandName}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
